import { useState } from "react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { BUSINESS } from "../data/business";
import { Facebook, Instagram, Mail, MapPin, Phone, Star } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Wypełnij wszystkie pola formularza.");
      return;
    }
    setSending(true);
    // Bezpośredni mailto — najprostsza i najskuteczniejsza forma bez backendu.
    const subject = encodeURIComponent(`Zapytanie ze strony BEAUTIX — ${form.name}`);
    const body = encodeURIComponent(
      `Imię: ${form.name}\nE-mail: ${form.email}\n\nWiadomość:\n${form.message}`,
    );
    window.location.href = `mailto:${BUSINESS.email}?subject=${subject}&body=${body}`;
    toast.success(
      "Otwieramy Twojego klienta pocztowego. Jeśli nic się nie stanie — zadzwoń do salonu.",
    );
    setTimeout(() => setSending(false), 1000);
  };

  return (
    <>
      <Seo
        title="Kontakt BEAUTIX Katowice — Ligocka 80 | Umów wizytę"
        description="Salon BEAUTIX Katowice ul. Ligocka 80. Zadzwoń: 794 000 270 lub 32 203 58 65. E-mail: beautixpolska@gmail.com. Sprawdź godziny otwarcia i dojazd."
        keywords="salon urody Katowice kontakt, BEAUTIX kontakt, salon Ligocka 80 Katowice, umów wizytę Katowice"
        path="/kontakt"
      />
      <Toaster position="bottom-right" richColors />

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-4">
        <Breadcrumbs items={[{ label: "Kontakt" }]} />
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <p className="eyebrow mb-4">Skontaktuj się z nami</p>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
            Zapiszmy Twój
            <br />
            <span className="italic text-[var(--beautix-pink)]">idealny</span> termin.
          </h1>
          <p className="mt-6 text-lg text-[var(--beautix-muted)] leading-relaxed font-light">
            Zadzwoń, napisz lub odwiedź nas osobiście — chętnie porozmawiamy
            o Twoich potrzebach i wybierzemy najlepszy zabieg.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex gap-4 items-start">
              <MapPin className="w-5 h-5 text-[var(--beautix-gold)] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-medium">{BUSINESS.name}</p>
                <p className="text-[var(--beautix-muted)]">
                  {BUSINESS.address.street}, {BUSINESS.address.postalCode}{" "}
                  {BUSINESS.address.city}
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <Phone className="w-5 h-5 text-[var(--beautix-gold)] mt-1 shrink-0" strokeWidth={1.5} />
              <div className="flex flex-col gap-1">
                {BUSINESS.phones.map((p) => (
                  <a
                    key={p.number}
                    href={`tel:${p.tel}`}
                    className="link-underline"
                    data-testid={`contact-${p.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <span className="text-[var(--beautix-muted)] text-sm">{p.label}: </span>
                    <span className="text-[var(--beautix-ink)] font-medium">{p.number}</span>
                  </a>
                ))}
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <Mail className="w-5 h-5 text-[var(--beautix-gold)] mt-1 shrink-0" strokeWidth={1.5} />
              <a
                href={`mailto:${BUSINESS.email}`}
                className="link-underline break-all"
                data-testid="contact-email"
              >
                {BUSINESS.email}
              </a>
            </div>
          </div>

          <div className="mt-10 flex gap-3">
            <a
              href={BUSINESS.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 border border-[var(--beautix-line)] rounded-full hover:border-[var(--beautix-pink)] hover:text-[var(--beautix-pink)] transition-colors"
              aria-label="Facebook BEAUTIX — najlepsze paznokcie w mieście"
              data-testid="contact-facebook-link"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a
              href={BUSINESS.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 border border-[var(--beautix-line)] rounded-full hover:border-[var(--beautix-pink)] hover:text-[var(--beautix-pink)] transition-colors"
              aria-label="Instagram BEAUTIX — @beautix_skn"
              data-testid="contact-instagram-link"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a
              href={BUSINESS.social.googleMaps}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 border border-[var(--beautix-line)] rounded-full hover:border-[var(--beautix-pink)] hover:text-[var(--beautix-pink)] transition-colors"
              aria-label="Zobacz BEAUTIX w Google Maps i przeczytaj opinie"
              data-testid="contact-google-maps-link"
            >
              <Star className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>

          <div className="mt-10">
            <p className="eyebrow mb-4">Godziny otwarcia</p>
            <ul className="space-y-3 max-w-sm">
              {BUSINESS.hours.map((h) => (
                <li key={h.day} className="flex justify-between text-sm border-b border-[var(--beautix-line)] pb-2">
                  <span>{h.day}</span>
                  <span className="text-[var(--beautix-muted)]">{h.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact form */}
        <div className="lg:col-span-6">
          <form
            onSubmit={submit}
            className="bg-[var(--beautix-cream)] p-8 md:p-10 border border-[var(--beautix-line)] space-y-6"
            data-testid="contact-form"
          >
            <p className="eyebrow mb-2">Formularz kontaktowy</p>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              Napisz do nas
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="c-name" className="text-xs tracking-[0.24em] uppercase text-[var(--beautix-muted)] mb-2 block">
                  Imię i nazwisko
                </label>
                <input
                  id="c-name"
                  data-testid="form-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white border border-[var(--beautix-line)] focus:border-[var(--beautix-pink)] outline-none px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label htmlFor="c-email" className="text-xs tracking-[0.24em] uppercase text-[var(--beautix-muted)] mb-2 block">
                  E-mail
                </label>
                <input
                  id="c-email"
                  data-testid="form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white border border-[var(--beautix-line)] focus:border-[var(--beautix-pink)] outline-none px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label htmlFor="c-msg" className="text-xs tracking-[0.24em] uppercase text-[var(--beautix-muted)] mb-2 block">
                  Wiadomość
                </label>
                <textarea
                  id="c-msg"
                  data-testid="form-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-white border border-[var(--beautix-line)] focus:border-[var(--beautix-pink)] outline-none px-4 py-3 text-sm resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="form-submit"
              className="w-full bg-[var(--beautix-pink)] hover:bg-[var(--beautix-pink-dark)] text-white px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
            >
              {sending ? "Wysyłanie..." : "Wyślij wiadomość"}
            </button>
            <p className="text-xs text-[var(--beautix-muted)]">
              Otwieramy Twoją aplikację pocztową z gotową wiadomością.
              Najszybciej odpowiadamy telefonicznie.
            </p>
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16 pt-4">
        <div className="max-w-3xl">
          <p className="eyebrow mb-3">Dojazd i praktyczne informacje</p>
          <h2 className="font-serif-display text-2xl md:text-3xl leading-tight mb-6">
            Jak do nas trafić?
          </h2>
          <div className="space-y-4 text-[var(--beautix-muted)] leading-relaxed font-light">
            <p>
              Studio BEAUTIX znajduje się przy ul. Ligockiej 80 w Katowicach,
              w dzielnicy Ligota-Panewniki. Salon jest łatwo dostępny zarówno
              samochodem, jak i komunikacją miejską — kilka minut piechotą
              od przystanku autobusowego Ligocka.
            </p>
            <p>
              <strong className="text-[var(--beautix-ink)]">Parking:</strong>{" "}
              Bezpłatne miejsca parkingowe dostępne przy salonie oraz w bocznych
              uliczkach. Nie musisz szukać płatnej strefy.
            </p>
            <p>
              <strong className="text-[var(--beautix-ink)]">Umawianie wizyt:</strong>{" "}
              Wszystkie usługi realizujemy po wcześniejszej rezerwacji telefonicznej.
              Zadzwoń pod {BUSINESS.phones[1].number} lub {BUSINESS.phones[0].number}.
              Podczas rozmowy dobierzemy odpowiedni czas na Twój zabieg — od
              szybkiego manicure express po dłuższą wizytę u podolog.
            </p>
            <p>
              <strong className="text-[var(--beautix-ink)]">Formy płatności:</strong>{" "}
              Akceptujemy gotówkę, karty płatnicze oraz BLIK. Wystawiamy również
              faktury na życzenie.
            </p>
            <p>
              <strong className="text-[var(--beautix-ink)]">Karty podarunkowe:</strong>{" "}
              Bony BEAUTIX na dowolną kwotę — świetny prezent na urodziny,
              Dzień Matki lub święta. Zamawiaj telefonicznie z minimum 24h
              wyprzedzeniem.
            </p>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="aspect-[16/9] w-full overflow-hidden border border-[var(--beautix-line)]">
          <iframe
            title="Mapa BEAUTIX Katowice Ligocka 80"
            src={BUSINESS.mapEmbed}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="google-map"
          />
        </div>
      </section>
    </>
  );
}
